import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

/**
 * Holds the globally-selected course filter so the TopBar dropdown (which lives
 * in the shared portal shell) and the Enrollments page (a separate route) stay
 * in sync.
 *
 *   selectedCourseId === ''  → "All courses" (no server-side filter applied)
 *   selectedCourseId === '42' → filter enrollments to that course id
 *
 * The selected value may persist as the user navigates between portal pages —
 * that's intentional and safe because it always defaults to '' (all). Enrollments
 * and Training read it for server-side list filtering. The dropdown itself is
 * hidden on routes where course filtering is irrelevant.
 */
const CourseFilterContext = createContext({
  selectedCourseId: '',
  selectedCourseName: '',
  setSelectedCourse: () => {},
});

export function CourseFilterProvider({ children }) {
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedCourseName, setSelectedCourseName] = useState('');

  // courseId '' (or nullish) means "All courses"; the name is kept only for
  // the trigger label so the dropdown can show the current selection.
  const setSelectedCourse = useCallback((courseId, courseName) => {
    setSelectedCourseId(courseId || '');
    setSelectedCourseName(courseId ? (courseName || '') : '');
  }, []);

  const value = useMemo(
    () => ({ selectedCourseId, selectedCourseName, setSelectedCourse }),
    [selectedCourseId, selectedCourseName, setSelectedCourse],
  );

  return (
    <CourseFilterContext.Provider value={value}>
      {children}
    </CourseFilterContext.Provider>
  );
}

export function useCourseFilter() {
  return useContext(CourseFilterContext);
}

export default CourseFilterContext;
